/**
 * Checkout Page
 * Main payment page where users are redirected to Flow for card registration
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckoutForm, LoadingSpinner, ErrorAlert } from '../components';
import { useCheckoutSession } from '../hooks';
import checkoutApi from '../lib/api';

type PaymentStatus = 'idle' | 'processing' | 'success' | 'error';

export default function CheckoutPage() {
  const { t, i18n } = useTranslation();
  // Extract sessionId from URL path
  const pathParts = window.location.pathname.split('/');
  const sessionId = pathParts[pathParts.length - 1] || '';

  // Extract params from query string
  const searchParams = new URLSearchParams(window.location.search);
  const organizationId = searchParams.get('organization_id') || undefined;
  const flowToken = searchParams.get('token'); // Token from Flow after payment

  const { session, loading, error, isExpired } = useCheckoutSession(sessionId, organizationId);

  // Payment processing state
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);

  // Set language from session
  useEffect(() => {
    if (session?.language && session.language !== i18n.language) {
      i18n.changeLanguage(session.language);
    }
  }, [session, i18n]);

  // Process Flow callback token
  useEffect(() => {
    if (!flowToken || !sessionId || paymentStatus !== 'idle') {
      return;
    }

    const processPayment = async () => {
      setPaymentStatus('processing');
      try {
        const result = await checkoutApi.verifyCardRegistration(sessionId, flowToken);
        setSubscriptionData(result);
        setPaymentStatus('success');

        // Clean URL (remove token)
        window.history.replaceState({}, '', `/checkout/${sessionId}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.paymentFailed');
        setPaymentError(message);
        setPaymentStatus('error');
      }
    };

    processPayment();
  }, [flowToken, sessionId, paymentStatus, t]);

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4">
        <div className="w-full max-w-md">
          <ErrorAlert message={t('checkout.errors.invalidOrMissingSessionId')} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4">
        <div className="w-full max-w-md">
          <LoadingSpinner message={t('checkout.processing')} />
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4">
        <div className="w-full max-w-md">
          <ErrorAlert
            message={t('checkout.errors.failedToLoadCheckout')}
            details={error || t('checkout.errors.failedToLoadCheckoutDetails')}
          />
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4">
        <div className="w-full max-w-md">
          <ErrorAlert
            message={t('checkout.errors.sessionExpired')}
            details={t('checkout.errors.sessionExpiredDetails')}
          />
        </div>
      </div>
    );
  }

  // Show processing state when verifying payment
  if (paymentStatus === 'processing') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4">
        <div className="w-full max-w-md">
          <LoadingSpinner message={t('checkout.processingPayment') || 'Procesando pago...'} />
        </div>
      </div>
    );
  }

  // Show success state after payment
  if (paymentStatus === 'success' && session && subscriptionData) {
    return (
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <div className="card w-full relative z-10">
          {/* Success Icon & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
              <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('payment.success.title') || '¡Pago Exitoso!'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('payment.success.subtitle') || 'Tu suscripción ha sido activada correctamente'}
            </p>
          </div>

          {/* Payment Details */}
          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('payment.plan') || 'Plan'}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{session.plan_snapshot.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('payment.amount') || 'Monto'}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    ${session.plan_snapshot.price.toLocaleString()} {session.plan_snapshot.currency}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('payment.date') || 'Fecha'}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {new Date().toLocaleDateString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('payment.transactionId') || 'ID Transacción'}</p>
                  <p className="font-mono text-xs font-semibold text-gray-900 dark:text-white break-all">
                    {subscriptionData.flow_subscription_id || subscriptionData.subscription_id}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('payment.email') || 'Confirmación enviada a'}</p>
              <p className="font-semibold text-gray-900 dark:text-white">{session.customer_email}</p>
            </div>
          </div>

          {/* Return to SaaS button */}
          {session.return_url && (
            <a
              href={session.return_url}
              className="btn-primary w-full py-3 text-center block"
            >
              {t('payment.success.returnButton') || `Volver a ${session.organization_name || 'la aplicación'}`}
            </a>
          )}
        </div>
      </div>
    );
  }

  // Show error state if payment failed
  if (paymentStatus === 'error') {
    return (
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
        <div className="card w-full relative z-10">
          {/* Error Icon & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 mb-6">
              <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('payment.error.title') || 'Error en el Pago'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {paymentError || t('payment.error.subtitle') || 'No se pudo procesar tu pago'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                setPaymentStatus('idle');
                setPaymentError(null);
                window.location.reload();
              }}
              className="btn-primary w-full py-3"
            >
              {t('payment.error.retryButton') || 'Intentar Nuevamente'}
            </button>
            {session?.return_url && (
              <a
                href={session.return_url}
                className="btn-secondary w-full py-3 text-center block"
              >
                {t('payment.error.cancelButton') || `Volver a ${session.organization_name || 'la aplicación'}`}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full md:min-h-[calc(100vh-64px)] md:flex md:flex-col">
      <div className="mb-4 md:hidden px-4 pt-2">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          {t('checkout.title')}
        </h1>
      </div>
      <div className="md:flex-1 md:flex md:items-center md:justify-center px-4 pt-0">
        <div className="w-full max-w-2xl">
          <CheckoutForm session={session} />
        </div>
      </div>
    </div>
  );
}
